import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Autocomplete, CircularProgress,
  RadioGroup, FormControlLabel, Radio, FormLabel,
  FormControl, Box, Typography, Divider, Chip, Grid, IconButton, Skeleton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import CustomizedSnackbars from '../../components/material-snackbars';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { getCalledMoveFields } from '../../services/kanban.service';
import { getCategoriesByOrganizationSimpleList } from '../../services/organizationcategory.service';
import {
  getTechinicalTeamsByOrganizationIdAndOrganizationCategoryId,
  getTechnicalUsersByTechnicalTeamIdAndRoleType,
  getCalledGridById,
  updateCalledBasicData,
} from '../../services/called.service';
import { createAccompaniment } from '../../services/accompaniment.service';

function KanbanFillFieldsModal({ open, calledId, called, errorMessage, lang, onClose, onSaveSuccess, onOptimisticClose, onMoveError, onBackgroundSuccess, userSession }) {
  const [loadingFields, setLoadingFields]         = useState(false);
  const [initialFields, setInitialFields]         = useState(null);

  const [categories, setCategories]               = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory]   = useState(null);

  const [teams, setTeams]                         = useState([]);
  const [loadingTeams, setLoadingTeams]           = useState(false);
  const [selectedTeam, setSelectedTeam]           = useState(null);

  const [users, setUsers]                         = useState([]);
  const [loadingUsers, setLoadingUsers]           = useState(false);
  const [selectedUser, setSelectedUser]           = useState(null);

  const [calledGridData, setCalledGridData]       = useState(null);

  const [accompanimentText, setAccompanimentText] = useState('');
  const [privacy, setPrivacy]                     = useState(false);
  const [submitting, setSubmitting]               = useState(false);
  const [saveError, setSaveError]                 = useState('');
  const [snackOpen, setSnackOpen]                 = useState(false);
  const [saveSnackOpen, setSaveSnackOpen]         = useState(false);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  useEffect(() => {
    if (!open || !calledId) return;

    setLoadingFields(true);
    setInitialFields(null);
    setCalledGridData(null);
    setSelectedCategory(null);
    setSelectedTeam(null);
    setSelectedUser(null);
    setAccompanimentText('');
    setPrivacy(false);
    setSaveError('');
    setSnackOpen(false);
    setSaveSnackOpen(false);

    Promise.all([
      getCalledMoveFields(calledId).catch(() => null),
      getCalledGridById(calledId).catch(() => null),
    ])
      .then(async ([moveRes, gridRes]) => {
        const cg = gridRes?.data ?? gridRes ?? null;
        if (cg?.id) setCalledGridData(cg);

        const fields = moveRes?.data?.data ?? null;
        setInitialFields(fields);

        if (!fields) return;

        const orgId  = fields.organizationid         ?? 0;
        const catId  = fields.organizationcategoryid ?? 0;
        const teamId = fields.technicalteamid        ?? 0;

        setLoadingCategories(true);
        if (!fields.assigneduserid) setLoadingTeams(true);

        const [catsBody, teamsBody] = await Promise.all([
          getCategoriesByOrganizationSimpleList(orgId).catch(() => null),
          !fields.assigneduserid
            ? getTechinicalTeamsByOrganizationIdAndOrganizationCategoryId(orgId, catId).catch(() => null)
            : Promise.resolve(null),
        ]);

        const cats = toArray(catsBody);
        setCategories(cats);
        setLoadingCategories(false);
        if (catId) setSelectedCategory(cats.find((c) => c.id === catId) ?? null);

        if (!fields.assigneduserid) {
          const tms = toArray(teamsBody);
          setTeams(tms);
          setLoadingTeams(false);
          if (teamId) setSelectedTeam(tms.find((t) => t.id === teamId) ?? null);

          if (teamId) {
            setLoadingUsers(true);
            const usersBody = await getTechnicalUsersByTechnicalTeamIdAndRoleType(teamId, 2).catch(() => null);
            const usrs = toArray(usersBody);
            setUsers(usrs);
            setLoadingUsers(false);
          }
        }
      })
      .finally(() => setLoadingFields(false));
  }, [open, calledId]);

  useEffect(() => {
    if (open && errorMessage) setSnackOpen(true);
  }, [open, errorMessage]);

  const handleCategoryChange = async (_e, value) => {
    setSelectedCategory(value);

    const userAlreadySet = !!(initialFields?.assigneduserid);
    setCalledGridData((prev) => prev ? {
      ...prev,
      organizationcategoryid: value?.id ?? null,
      ...(userAlreadySet ? {} : { techinicalteamid: null, assigneduserid: null }),
    } : prev);

    if (userAlreadySet) return;

    setSelectedTeam(null);
    setSelectedUser(null);
    setUsers([]);

    if (!initialFields) return;

    const orgId = initialFields.organizationid ?? 0;
    const catId = value?.id ?? 0;

    setLoadingTeams(true);
    const teamsBody = await getTechinicalTeamsByOrganizationIdAndOrganizationCategoryId(orgId, catId).catch(() => null);
    setTeams(toArray(teamsBody));
    setLoadingTeams(false);
  };

  const handleTeamChange = async (_e, value) => {
    setSelectedTeam(value);
    setSelectedUser(null);
    setUsers([]);
    setCalledGridData((prev) => prev ? { ...prev, techinicalteamid: value?.id ?? null, assigneduserid: null } : prev);

    if (!value) return;

    setLoadingUsers(true);
    const usersBody = await getTechnicalUsersByTechnicalTeamIdAndRoleType(value.id, 2).catch(() => null);
    setUsers(toArray(usersBody));
    setLoadingUsers(false);
  };

  const handleConfirm = async () => {
    setSaveError('');

    // Validação de campos obrigatórios antes de chamar a API
    if (!categoryDisabled && !selectedCategory) {
      setSaveError('É necessário selecionar uma categoria.');
      setSaveSnackOpen(true);
      return;
    }
    if (needsAssignedUser && !selectedUser) {
      setSaveError('É necessário selecionar um responsável.');
      setSaveSnackOpen(true);
      return;
    }

    setSubmitting(true);

    const extraCardData = {
      ...(selectedCategory?.name ? { categoryName: selectedCategory.name } : {}),
      ...(selectedUser ? { assignedUserName: selectedUser.user?.name ?? selectedUser.name ?? '' } : {}),
    };

    const cg = calledGridData;
    if (!cg || !cg.id) {
      setSaveError('Não foi possível carregar os dados do chamado.');
      setSaveSnackOpen(true);
      setSubmitting(false);
      return;
    }

    const basicFormData = new FormData();
    basicFormData.append('Id',                   calledId);
    basicFormData.append('Title',                cg.title ?? '');
    basicFormData.append('Description',          cg.description ?? ' ');
    basicFormData.append('Type',                 cg.typeid ?? 0);
    basicFormData.append('OrganizationCategory', cg.organizationcategoryid ?? 0);
    basicFormData.append('AssignedUser',         cg.assigneduserid ?? 0);
    basicFormData.append('TechinicalTeamId',     cg.techinicalteamid ?? 0);
    basicFormData.append('AddressId',            cg.addressid ?? 0);
    basicFormData.append('RequestingUserId',     cg.requestinguserid ?? 0);
    basicFormData.append('UpdatedUser',          userSession?.user?.id ?? 0);
    if (cg.duedate) basicFormData.append('DueDate', cg.duedate);

    let accFormData = null;
    if (accompanimentText.trim()) {
      accFormData = new FormData();
      accFormData.append('Called',                     calledId);
      accFormData.append('CalledTitle',                cg.title ?? '');
      accFormData.append('Description',                accompanimentText.trim());
      accFormData.append('CalledDescription',          cg.description ?? ' ');
      accFormData.append('CalledOrganizationCategory', cg.organizationcategoryid ?? 0);
      accFormData.append('CalledType',                 cg.typeid ?? 0);
      accFormData.append('CalledStatus',               cg.statusid ?? 0);
      accFormData.append('CalledAssignedUser',         cg.assigneduserid ?? 0);
      accFormData.append('CalledAddressId',            cg.addressid ?? 0);
      accFormData.append('CalledObservantUser',        0);
      accFormData.append('UserAction',                 userSession?.user?.id ?? 0);
      accFormData.append('Privacy',                    privacy);
    }

    const calls = [updateCalledBasicData(basicFormData)];
    if (accFormData) calls.push(createAccompaniment(accFormData));

    let timedOut = false;
    const apiPromise = Promise.all(calls);

    timerRef.current = setTimeout(() => {
      timedOut = true;
      setSubmitting(false);
      onOptimisticClose?.(extraCardData);
      apiPromise
        .then(([saveRes]) => {
          if (saveRes?.data?.success) {
            onBackgroundSuccess?.(calledId, extraCardData);
          } else {
            const errMsg = saveRes?.data?.errors?.[0] ?? 'Erro ao salvar campos';
            onMoveError?.(calledId, errMsg);
          }
        })
        .catch(() => {
          onMoveError?.(calledId, 'Erro ao salvar. Tente novamente.');
        });
    }, 5000);

    try {
      const [saveRes] = await apiPromise;
      clearTimeout(timerRef.current);
      if (timedOut) return;

      if (!saveRes?.data?.success) {
        clearTimeout(timerRef.current);
        setSaveError(saveRes?.data?.errors?.[0] ?? 'Erro ao salvar campos');
        setSaveSnackOpen(true);
        setSubmitting(false);
        return;
      }

      onSaveSuccess(extraCardData);
    } catch {
      clearTimeout(timerRef.current);
      if (timedOut) return;
      setSaveError('Erro ao salvar. Tente novamente.');
      setSaveSnackOpen(true);
    } finally {
      if (!timedOut) setSubmitting(false);
    }
  };

  // API serializes all keys in lowercase
  const categoryDisabled    = !!(initialFields?.organizationcategoryid);
  const teamDisabled        = !!(initialFields?.technicalteamid);
  const needsAssignedUser   = !loadingFields && initialFields != null && !initialFields.assigneduserid;

  const priorityColors = {
    alta:  { bg: '#fee2e2', color: '#ef4444' },
    média: { bg: '#fff7cd', color: '#f59e0b' },
    baixa: { bg: '#d1fae5', color: '#10b981' },
  };
  const priorityStyle = priorityColors[(called?.priorityName ?? '').toLowerCase()] ?? { bg: '#f3f4f6', color: '#6b7280' };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} disableEscapeKeyDown={submitting} fullWidth maxWidth="md" scroll="paper" PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6">Preencher campos obrigatórios</Typography>
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3, overflowY: 'auto', maxHeight: 'calc(90vh - 140px)' }}>

        {submitting && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Skeleton variant="text" height={24} width="40%" />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="text" height={20} width="30%" />
            <Skeleton variant="rectangular" height={100} />
          </Box>
        )}

        {!submitting && (
        <>
        {called && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: called.statusColorHex ?? '#6b7280', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2">{called.statusName}</Typography>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              <a
                href={`/${lang}/called-accompaniment/${called.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#1976d2', textDecoration: 'underline' }}
              >
                #{called.id} — {called.title}
              </a>
            </Typography>

            {called.priorityName && (
              <Chip
                label={called.priorityName}
                size="small"
                sx={{ mb: 2, bgcolor: priorityStyle.bg, color: priorityStyle.color }}
              />
            )}

            <Grid container spacing={2} sx={{ mb: 1 }}>
              {called.organizationName && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Cliente</Typography>
                  <Typography variant="body1">{called.organizationName}</Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Categoria</Typography>
                <Typography variant="body1">{called.categoryName || 'Não definida'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Responsável</Typography>
                <Typography variant="body1">{called.assignedUserName || 'Não atribuído'}</Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        <Divider sx={{ mb: 2.5 }} />

        {loadingFields ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Autocomplete
              options={categories}
              getOptionLabel={(opt) => opt.name ?? ''}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={categoryDisabled}
              loading={loadingCategories}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCategories && <CircularProgress color="inherit" size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {needsAssignedUser && (
              <Autocomplete
                options={teams}
                getOptionLabel={(opt) => opt.name ?? ''}
                isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                value={selectedTeam}
                onChange={handleTeamChange}
                disabled={teamDisabled}
                loading={loadingTeams}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Time de atendimento"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingTeams && <CircularProgress color="inherit" size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {needsAssignedUser && (
              <Autocomplete
                options={users}
                getOptionLabel={(opt) => opt.user?.name ?? opt.name ?? ''}
                isOptionEqualToValue={(opt, val) => opt.userid === val?.userid}
                value={selectedUser}
                onChange={(_e, val) => {
                  setSelectedUser(val);
                  setCalledGridData((prev) => prev ? { ...prev, assigneduserid: val?.userid ?? null } : prev);
                }}
                disabled={!selectedTeam}
                loading={loadingUsers}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers && <CircularProgress color="inherit" size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Acompanhamento (opcional)
            </Typography>

            <TextField
              multiline
              minRows={4}
              fullWidth
              size="small"
              label="Descrição do acompanhamento"
              value={accompanimentText}
              onChange={(e) => setAccompanimentText(e.target.value)}
            />

            <FormControl>
              <FormLabel component="legend" sx={{ fontSize: 13 }}>Visibilidade</FormLabel>
              <RadioGroup
                row
                value={privacy ? 'private' : 'public'}
                onChange={(e) => setPrivacy(e.target.value === 'private')}
              >
                <FormControlLabel value="public"  control={<Radio size="small" />} label="Ação pública" />
                <FormControlLabel value="private" control={<Radio size="small" />} label="Ação privada" />
              </RadioGroup>
            </FormControl>

          </Box>
        )}
        </>
        )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting || loadingFields}
          variant="contained"
          sx={{ borderRadius: 2 }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Confirmar'}
        </Button>
      </DialogActions>

      <CustomizedSnackbars
        variant="warning"
        message={errorMessage}
        isOpen={snackOpen}
        toClose={() => setSnackOpen(false)}
      />
      <CustomizedSnackbars
        variant="error"
        message={saveError}
        isOpen={saveSnackOpen}
        toClose={() => setSaveSnackOpen(false)}
      />
    </Dialog>
  );
}

function toArray(body) {
  const list = body?.data ?? [];
  return Array.isArray(list) ? list : [];
}

const mapStateToProps = (state) => ({ userSession: state.userSession });
const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(KanbanFillFieldsModal);
